import React, { useCallback, useState } from 'react'
import { Pagination } from 'transfix-ui/components'
import { makeStyles } from 'transfix-ui/core'
import { StringParam, withDefault } from 'use-query-params'
import { ShipperFilterForm } from 'apps/marketplace/pages/shippers/components/ShipperList/components'
import { TShipperFilterSchema } from 'apps/marketplace/pages/shippers/components/ShipperList/components/ShipperFilterForm/schemas'
import { TPageCursor } from 'apps/marketplace/pages/shippers/components/ShipperList/types'
import {
  IShippersQueryVariables,
  useShippersQuery,
} from 'apps/marketplace/pages/shippers/components/ShippersPage/graphql/Shippers'
import { useQueryParams } from 'utils/hooks'
import ShipperConfigDetailsTable from './ShipperConfigDetailsTable'

const useShipperConfigListStyles = makeStyles(theme => ({
  paginationContainer: {
    textAlign: 'center',
    marginTop: theme.spacing(8),
  },
}))

const ITEMS_PER_PAGE = 20

export const ShipperConfigList = () => {
  const classes = useShipperConfigListStyles()
  const [currentPageCursorInfo, setCurrentPageCursorInfo] = useState<TPageCursor>({})
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)
  const [filters, setFilters] = useQueryParams<
    Pick<IShippersQueryVariables, 'searchTerm' | 'state'>
  >({
    searchTerm: withDefault(StringParam, ''),
    state: withDefault(StringParam, ''),
  })

  const { data, error, loading } = useShippersQuery({
    variables: {
      state: undefined,
      searchTerm: filters.searchTerm,
      first: ITEMS_PER_PAGE,
      after: currentPageCursorInfo.after,
      before: currentPageCursorInfo.before,
    },
  })

  const { searchShipperApplications } = data || {}
  const { nodes: items = [], pageInfo, totalCount = 0 } = searchShipperApplications || {}
  const { endCursor, startCursor } = pageInfo || {}

  const pageChangeHandler = (newPageNumber: number) => {
    const goNext = newPageNumber > currentPageNumber

    if (goNext) {
      setCurrentPageCursorInfo({ after: endCursor })
    } else {
      setCurrentPageCursorInfo({ before: startCursor })
    }

    setCurrentPageNumber(newPageNumber)
  }

  const filterChangeHandler = useCallback(
    (filters: TShipperFilterSchema) => {
      const { searchTerm: searchObj, state } = filters || {}
      const { text: searchTerm } = searchObj || {}
      setFilters({
        state,
        searchTerm,
      })
      setCurrentPageNumber(1)
      setCurrentPageCursorInfo({})
    },
    [setFilters]
  )

  const clearFiltersHandler = useCallback(() => {
    setFilters({ searchTerm: '', state: '' })
    setCurrentPageNumber(1)
    setCurrentPageCursorInfo({})
  }, [setFilters])

  return (
    <div>
      <ShipperFilterForm
        defaultValues={filters}
        onSubmitSuccess={filterChangeHandler}
        onReset={clearFiltersHandler}
      />
      <ShipperConfigDetailsTable error={error} items={items || []} isLoading={false} />
      <div className={classes.paginationContainer}>
        <Pagination
          currentPage={currentPageNumber}
          loading={loading}
          numberOfPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
          onPageChange={pageChangeHandler}
        />
      </div>
    </div>
  )
}
